import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { heroiconsCheckBadgeFilled } from '@ld.consumer-portal/data-uri';
import {
  ContactInformationService,
  DashboardService,
  MlaAuthService,
  MlaBorrowerService,
} from '@ld.consumer-portal/data-access';
import { MfaOption } from '@ld.consumer-portal/mfa';
import {
  AUTH_CONTEXT,
  ContactInfoRequest,
  CustomerIdentityAuthenticationMethod,
  CustomerIdentityTenantConfig,
  ENVIRONMENT,
  Environment,
  MlaBorrowerInfo,
  UserProfile,
} from '@ld.consumer-portal/models';
import { SvgIconComponent } from '@ld.consumer-portal/ui-shared';
import {
  AuthService,
  CiamAuthService,
  CustomerIdentityService,
  KeyboardService,
  LoggingService,
  MfaStepUpService,
  PlatformService,
} from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export type MfaPreferencesStep = 'verify' | 'form' | 'success';

const MFA_EMAIL_FIELD_NAMES = [
  'MFAEmailPrimary',
  'MFAEmailAlternate',
  'MFAEmailTertiary',
  'MFAEmailQuaternary',
  'MFAEmailQuintenary',
];

const MFA_PHONE_FIELD_NAMES = [
  'MFAPhonePrimary',
  'MFAPhoneAlternate',
  'MFAPhoneTertiary',
  'MFAPhoneQuaternary',
  'MFAPhoneQuintenary',
];

interface MfaPropertyEntry {
  fieldName: string;
  value: string;
}

interface ExternalMfaSources {
  servicingPhones: string[];
  servicingEmails: string[];
  originationPhones: string[];
}

@Component({
  selector: 'ld-mfa-preferences-modal',
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    TranslocoDirective,
    SvgIconComponent,
    BaseModalComponent,
    UpperCasePipe,
  ],
  templateUrl: './mfa-preferences-modal.component.html',
  styleUrl: './mfa-preferences-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaPreferencesModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly customerIdentityService = inject(CustomerIdentityService);
  private readonly contactInformationService = inject(ContactInformationService);
  private readonly dashboardService = inject(DashboardService);
  private readonly mlaBorrowerService = inject(MlaBorrowerService);
  private readonly mlaAuthService = inject(MlaAuthService);
  private readonly authService = inject(AuthService);
  private readonly ciamAuthService = inject(CiamAuthService);
  private readonly mfaStepUpService = inject(MfaStepUpService);
  private readonly loggingService = inject(LoggingService);
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly keyboardService = inject(KeyboardService);
  private readonly environment = inject<Environment>(ENVIRONMENT);

  protected readonly isBottomSheet = this.platformService.isNativeMobile;
  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  protected readonly checkBadgeIcon = heroiconsCheckBadgeFilled;
  protected readonly supportPhoneNumber = this.environment.appSettings.servicingCustomerCare?.phone.display ?? '';
  protected readonly supportPhoneNumberTel = this.environment.appSettings.servicingCustomerCare?.phone.link ?? '';

  isOpen = input<boolean>(false);
  closeModal = output<void>();

  protected readonly currentStep = signal<MfaPreferencesStep>('verify');
  protected readonly identityVerified = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly mfaLoading = signal(false);
  protected readonly verifyLoading = signal(false);
  protected readonly mfaError = signal<string | null>(null);
  protected readonly phoneError = signal<string | null>(null);
  protected readonly emailError = signal<string | null>(null);

  protected readonly phoneFormOptions = signal<MfaOption[]>([]);
  protected readonly emailFormOptions = signal<MfaOption[]>([]);
  protected readonly defaultPhoneMethodId = signal('');
  protected readonly defaultEmailMethodId = signal('');
  protected readonly methodsLoaded = signal(false);

  // Gate the phone/email selectors on whether the AuthenticationMethods response
  // (`details.methods[]`) actually contains a valid method of that type.
  protected readonly showPhoneField = signal(false);
  protected readonly showEmailField = signal(false);

  // When methods have loaded but neither a valid phone nor email exists on file,
  // the user cannot set MFA preferences and must contact customer support.
  protected readonly noMethodsAvailable = computed(
    () => this.methodsLoaded() && !this.showPhoneField() && !this.showEmailField(),
  );

  protected readonly descriptionKey = computed(() => {
    if (!this.showPhoneField()) return 'mfaPreferencesEmailOnlyDescription';
    if (!this.showEmailField()) return 'mfaPreferencesPhoneOnlyDescription';
    return 'mfaPreferencesDescription';
  });

  private readonly phoneFieldNameByValue = new Map<string, string>();
  private readonly emailFieldNameByValue = new Map<string, string>();
  private readonly entraOnlyPhoneValues = new Set<string>();
  private readonly entraOnlyEmailValues = new Set<string>();
  private servicingPhoneValues: string[] = [];
  private servicingEmailValues: string[] = [];
  private originationPhoneValues: string[] = [];

  protected readonly form: FormGroup = this.fb.group({
    phoneMethodId: [''],
    emailMethodId: [''],
  });

  protected readonly modalTitle = computed(() => {
    const step = this.currentStep();
    switch (step) {
      case 'verify':
        return this.transloco.translate('mfaPreferencesMfaChallengeTitle');
      case 'form':
        return this.transloco.translate('mfaPreferencesTitle');
      case 'success':
        return this.transloco.translate('mfaPreferencesSuccessTitle');
    }
  });

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.initializeMfa());
      }
    });
  }

  private async initializeMfa(): Promise<void> {
    this.mfaLoading.set(true);
    this.mfaError.set(null);
    this.identityVerified.set(false);
    this.currentStep.set('verify');

    try {
      await this.ciamAuthService.ensureInitialized();
      await this.populateFormFromMfaProperties();

      if (!this.methodsLoaded()) {
        this.mfaError.set(this.transloco.translate('mfaPreferencesLoadError'));
      } else if (this.noMethodsAvailable()) {
        // No phone or email is on file, so there is nothing to challenge against for
        // identity verification. Skip the verify step and go straight to the form step,
        // which renders the "contact support" message instead of the selectors.
        this.currentStep.set('form');
      }
    } catch (error) {
      this.loggingService.logException(error, '[MfaPreferences] Failed to initialize MFA');
      this.mfaError.set(this.transloco.translate('mfaPreferencesLoadError'));
    } finally {
      this.mfaLoading.set(false);
    }
  }

  private getEntraTenantId(): string {
    const storedConfig = sessionStorage.getItem('ciam_tenant_config');
    if (storedConfig) {
      const config = JSON.parse(storedConfig) as CustomerIdentityTenantConfig;
      return config.entraTenantID ?? '';
    }
    return '';
  }

  private async populateFormFromMfaProperties(): Promise<void> {
    const tenantGuid = this.getEntraTenantId();
    const externalUserId = this.ciamAuthService.getExternalUserId();

    if (!tenantGuid || !externalUserId) {
      this.loggingService.logWarning(`[MfaPreferences] Missing tenantGuid or externalUserId for MFA properties lookup`);
      return;
    }

    try {
      const [propertiesResult, currentMethods, externalSources] = await Promise.all([
        this.customerIdentityService.getUserProperties(externalUserId, 'MFA'),
        this.customerIdentityService.getAuthenticationMethods(externalUserId, tenantGuid).catch((err) => {
          this.loggingService.logWarning(`[MfaPreferences] Failed to load current auth methods: ${err}`);
          return { methods: [] };
        }),
        this.loadExternalMfaSources(),
      ]);

      const parsed = this.parseMfaProperties(propertiesResult.propertiesJson);

      this.servicingPhoneValues = externalSources.servicingPhones;
      this.servicingEmailValues = externalSources.servicingEmails;
      this.originationPhoneValues = externalSources.originationPhones;

      const phones = this.appendPhoneOptions(this.mapPhoneEntriesToOptions(parsed.phones), [
        ...this.servicingPhoneValues,
        ...this.originationPhoneValues,
      ]);
      const emails = this.appendEmailOptions(this.mapEmailEntriesToOptions(parsed.emails), this.servicingEmailValues);

      const allPhones = this.mergeEntraPhoneOptions(phones, currentMethods.methods);
      const allEmails = this.mergeEntraEmailOptions(emails, currentMethods.methods);

      this.phoneFormOptions.set(allPhones);
      this.emailFormOptions.set(allEmails);

      // Phone/email options are already filtered to entries that contain a valid
      // number/address upstream, so a non-empty list is sufficient to show the field.
      this.showPhoneField.set(allPhones.length > 0);
      this.showEmailField.set(allEmails.length > 0);

      const currentPhone = currentMethods.methods.find((m) => m.phoneNumber);
      const currentEmail = currentMethods.methods.find((m) => m.emailAddress);

      const defaultPhoneId =
        this.findMatchingPhone(allPhones, currentPhone?.phoneNumber) ??
        (allPhones.length > 0 ? allPhones[0].value : '');
      const defaultEmailId =
        this.findMatchingEmail(allEmails, currentEmail?.emailAddress) ??
        (allEmails.length > 0 ? allEmails[0].value : '');

      this.defaultPhoneMethodId.set(defaultPhoneId);
      this.defaultEmailMethodId.set(defaultEmailId);

      this.form.patchValue({
        phoneMethodId: defaultPhoneId,
        emailMethodId: defaultEmailId,
      });

      this.methodsLoaded.set(true);
    } catch (error) {
      this.loggingService.logException(error, '[MfaPreferences] Failed to load MFA user properties');
    }
  }

  private async loadExternalMfaSources(): Promise<ExternalMfaSources> {
    const [servicing, originationPhones] = await Promise.all([
      this.loadActiveServicingContacts().catch((error) => {
        this.loggingService.logException(error, '[MfaPreferences] Failed to load active servicing contacts');
        return { phones: [], emails: [] };
      }),
      this.loadOriginationPhoneNumbers().catch((error) => {
        this.loggingService.logException(error, '[MfaPreferences] Failed to load origination phone numbers');
        return [];
      }),
    ]);

    return {
      servicingPhones: servicing.phones,
      servicingEmails: servicing.emails,
      originationPhones,
    };
  }

  private async loadActiveServicingContacts(): Promise<{ phones: string[]; emails: string[] }> {
    const loanResponses = this.dashboardService.loanResponses();
    const userProfile = this.dashboardService.userProfile();
    const requests = this.buildActiveContactInfoRequests(loanResponses, userProfile);

    if (!requests.length || !userProfile) {
      return { phones: [], emails: [] };
    }

    const contacts = await this.getContactInformationWithFallback(requests, userProfile.userProfileID || '');

    return {
      phones: this.uniquePhones(
        contacts.flatMap((contact) =>
          (contact.telecomInfo ?? [])
            .filter((telecom) => telecom.type === 'Cell' && !telecom.isDeleted)
            .map((telecom) => telecom.telecomNumber),
        ),
      ),
      emails: this.uniqueEmails(contacts.map((contact) => contact.emailAddress)),
    };
  }

  private async getContactInformationWithFallback(
    requests: ContactInfoRequest[],
    userProfileId: string,
  ): Promise<Awaited<ReturnType<ContactInformationService['getContactInformation']>>> {
    try {
      return await this.contactInformationService.getContactInformation(requests, userProfileId);
    } catch (batchError) {
      const results = await Promise.allSettled(
        requests.map((request) => this.contactInformationService.getContactInformation([request], userProfileId)),
      );

      const contacts = results.flatMap((result) => {
        if (result.status === 'fulfilled') return result.value;

        this.loggingService.logException(result.reason, '[MfaPreferences] Failed to load one active servicing contact');
        return [];
      });

      if (results.every((result) => result.status === 'rejected')) throw batchError;

      return contacts;
    }
  }

  private buildActiveContactInfoRequests(
    loanResponses: ReturnType<DashboardService['loanResponses']>,
    userProfile: UserProfile | null,
  ): ContactInfoRequest[] {
    if (!userProfile) return [];

    return loanResponses
      .filter(({ loan }) => loan.isActive)
      .map(({ loan }) => {
        const loanProperty = userProfile.properties.find(
          (property) => property.key === 'LoanNumber' && property.value === loan.loanNumber,
        );
        if (!loanProperty) return null;

        const valueFor = (key: string): string | undefined =>
          userProfile.properties.find(
            (property) => property.key === key && property.attributeCollectionId === loanProperty.attributeCollectionId,
          )?.value;

        const encryptionValue = valueFor('AuthorizationEncryptionValue');
        const lastSSN = valueFor('LastSSN');
        const zipCode = valueFor('ZipCode');
        if (!encryptionValue || !lastSSN || !zipCode) return null;

        return {
          loanNumber: loan.loanNumber,
          encryptionValue,
          lastSSN,
          zipCode,
        } satisfies ContactInfoRequest;
      })
      .filter((request): request is ContactInfoRequest => request !== null);
  }

  private async loadOriginationPhoneNumbers(): Promise<string[]> {
    const borrowers = await this.mlaBorrowerService.getBorrowerInfo(
      [
        'borrowerInfoId',
        'borrowerIds',
        'cpBorrowerIds',
        'firstName',
        'lastName',
        'email',
        'phone',
        'ssnVerify',
        'isPrimary',
        'borrowerToken',
        'loanDetail',
        'mostRecentPhoneNumber',
        'phoneNumbers',
      ],
      true,
    );

    const borrower = this.resolveAccountBorrower(borrowers);
    if (!borrower) return [];

    return this.uniquePhones([
      ...(borrower.phoneNumbers ?? []),
      borrower.mostRecentPhoneNumber ?? '',
      borrower.phone ?? '',
    ]);
  }

  private resolveAccountBorrower(borrowers: MlaBorrowerInfo[]): MlaBorrowerInfo | null {
    if (!borrowers.length) return null;

    const selectedId = this.mlaAuthService.getSelectedBorrowerId();
    if (selectedId) {
      const selected = borrowers.find((borrower) =>
        [borrower.borrowerInfoId, ...(borrower.borrowerIds ?? []), ...(borrower.cpBorrowerIds ?? [])]
          .filter(Boolean)
          .includes(selectedId),
      );
      if (selected) return selected;
    }

    const accountEmail = this.authService.getUserEmail()?.trim().toLowerCase();
    const emailMatches = accountEmail
      ? borrowers.filter((borrower) => borrower.email?.trim().toLowerCase() === accountEmail)
      : [];
    if (emailMatches.length === 1) return emailMatches[0];

    const selectedName = this.mlaAuthService.getSelectedBorrowerName();
    if (selectedName) {
      const nameMatches = borrowers.filter(
        (borrower) =>
          borrower.firstName.trim().toLowerCase() === selectedName.firstName.trim().toLowerCase() &&
          borrower.lastName.trim().toLowerCase() === selectedName.lastName.trim().toLowerCase(),
      );
      if (nameMatches.length === 1) return nameMatches[0];
    }

    return borrowers.length === 1 ? borrowers[0] : null;
  }

  private mergeEntraPhoneOptions(
    ciPhones: MfaOption[],
    entraMethods: CustomerIdentityAuthenticationMethod[],
  ): MfaOption[] {
    this.entraOnlyPhoneValues.clear();

    const seenDigits = new Set(ciPhones.map((phone) => this.normalizePhoneDigits(phone.value)));
    const entraOnlyPhones: MfaOption[] = [];

    for (const method of entraMethods) {
      if (!method.phoneNumber) continue;

      const digits = this.normalizePhoneDigits(method.phoneNumber);
      if (!digits || seenDigits.has(digits)) continue;

      seenDigits.add(digits);
      this.entraOnlyPhoneValues.add(method.phoneNumber);
      entraOnlyPhones.push({
        value: method.phoneNumber,
        label: this.maskPhoneNumber(method.phoneNumber),
        masked: this.maskPhoneNumber(method.phoneNumber),
      });
    }

    return [...ciPhones, ...entraOnlyPhones];
  }

  private mergeEntraEmailOptions(
    ciEmails: MfaOption[],
    entraMethods: CustomerIdentityAuthenticationMethod[],
  ): MfaOption[] {
    this.entraOnlyEmailValues.clear();

    const seenEmails = new Set(ciEmails.map((email) => email.value.toLowerCase()));
    const entraOnlyEmails: MfaOption[] = [];

    for (const method of entraMethods) {
      if (!method.emailAddress) continue;

      const key = method.emailAddress.toLowerCase();
      if (seenEmails.has(key)) continue;

      seenEmails.add(key);
      this.entraOnlyEmailValues.add(method.emailAddress);
      entraOnlyEmails.push({
        value: method.emailAddress,
        label: method.emailAddress,
        masked: method.emailAddress,
      });
    }

    return [...ciEmails, ...entraOnlyEmails];
  }

  private findMatchingPhone(options: MfaOption[], entraPhone?: string): string | null {
    if (!entraPhone) return null;

    const entraDigits = this.normalizePhoneDigits(entraPhone);
    const match = options.find((opt) => this.normalizePhoneDigits(opt.value) === entraDigits);
    return match?.value ?? null;
  }

  private findMatchingEmail(options: MfaOption[], entraEmail?: string): string | null {
    if (!entraEmail) return null;

    const expected = entraEmail.toLowerCase();
    const match = options.find((opt) => opt.value.toLowerCase() === expected);
    return match?.value ?? null;
  }

  protected async onContinueVerify(): Promise<void> {
    if (this.verifyLoading()) return;

    const externalUserId = this.ciamAuthService.getExternalUserId();

    if (!externalUserId || !this.methodsLoaded()) {
      this.mfaError.set(this.transloco.translate('errorsIdentityVerificationFailed'));
      return;
    }

    this.mfaError.set(null);
    this.verifyLoading.set(true);

    try {
      await this.mfaStepUpService.stepUpWithCurrentMethods(AUTH_CONTEXT.SENSITIVE_OPERATION);
      this.identityVerified.set(true);
      this.currentStep.set('form');
    } catch (error) {
      this.loggingService.logException(error, '[MfaPreferences] Identity verification failed');
      this.mfaError.set(this.transloco.translate('errorsIdentityVerificationFailed'));
    } finally {
      this.verifyLoading.set(false);
    }
  }

  private parseMfaProperties(propertiesJson: string): { emails: MfaPropertyEntry[]; phones: MfaPropertyEntry[] } {
    const emails: MfaPropertyEntry[] = [];
    const phones: MfaPropertyEntry[] = [];

    for (const instance of this.extractMfaInstances(propertiesJson)) {
      for (const [fieldName, rawValue] of Object.entries(instance)) {
        if (fieldName === 'groupInstanceId' || typeof rawValue !== 'string') continue;

        const value = rawValue.trim();
        if (!value) continue;

        if (fieldName.startsWith('MFAEmail')) {
          emails.push({ fieldName, value });
        } else if (fieldName.startsWith('MFAPhone')) {
          phones.push({ fieldName, value });
        }
      }
    }

    return {
      emails: this.sortByFieldOrder(emails, MFA_EMAIL_FIELD_NAMES),
      phones: this.sortByFieldOrder(phones, MFA_PHONE_FIELD_NAMES),
    };
  }

  private extractMfaInstances(propertiesJson: string): Record<string, unknown>[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(propertiesJson);
    } catch {
      return [];
    }

    if (!this.isRecord(parsed)) return [];

    const groups = parsed['groups'];
    if (!this.isRecord(groups)) return [];

    const mfa = groups['MFA'];
    if (!Array.isArray(mfa)) return [];

    return mfa.filter((instance): instance is Record<string, unknown> => this.isRecord(instance));
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private sortByFieldOrder(entries: MfaPropertyEntry[], fieldOrder: string[]): MfaPropertyEntry[] {
    const orderIndex = (fieldName: string): number => {
      const index = fieldOrder.indexOf(fieldName);
      return index === -1 ? fieldOrder.length : index;
    };
    return [...entries].sort((a, b) => orderIndex(a.fieldName) - orderIndex(b.fieldName));
  }

  private mapEmailEntriesToOptions(entries: MfaPropertyEntry[]): MfaOption[] {
    this.emailFieldNameByValue.clear();

    const options: MfaOption[] = [];
    const seen = new Set<string>();

    for (const entry of entries) {
      const key = entry.value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      this.emailFieldNameByValue.set(entry.value, entry.fieldName);
      options.push({ value: entry.value, label: entry.value, masked: entry.value });
    }

    return options;
  }

  private mapPhoneEntriesToOptions(entries: MfaPropertyEntry[]): MfaOption[] {
    this.phoneFieldNameByValue.clear();

    const options: MfaOption[] = [];
    const seen = new Set<string>();

    for (const entry of entries) {
      const digits = this.normalizePhoneDigits(entry.value);
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);

      this.phoneFieldNameByValue.set(entry.value, entry.fieldName);
      options.push({
        value: entry.value,
        label: this.maskPhoneNumber(entry.value),
        masked: this.maskPhoneNumber(entry.value),
      });
    }

    return options;
  }

  private appendPhoneOptions(options: MfaOption[], values: string[]): MfaOption[] {
    const result = [...options];
    const seen = new Set(options.map((option) => this.normalizePhoneDigits(option.value)));

    for (const value of values) {
      const digits = this.normalizePhoneDigits(value);
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);
      result.push({ value, label: this.maskPhoneNumber(value), masked: this.maskPhoneNumber(value) });
    }

    return result;
  }

  private appendEmailOptions(options: MfaOption[], values: string[]): MfaOption[] {
    const result = [...options];
    const seen = new Set(options.map((option) => option.value.trim().toLowerCase()));

    for (const value of values) {
      const email = value.trim();
      const key = email.toLowerCase();
      if (!email || seen.has(key)) continue;
      seen.add(key);
      result.push({ value: email, label: email, masked: email });
    }

    return result;
  }

  private uniquePhones(values: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const value of values) {
      const trimmed = value?.trim();
      const digits = trimmed ? this.normalizePhoneDigits(trimmed) : '';
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);
      result.push(trimmed);
    }
    return result;
  }

  private uniqueEmails(values: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const value of values) {
      const trimmed = value?.trim();
      const key = trimmed?.toLowerCase();
      if (!trimmed || !key || seen.has(key)) continue;
      seen.add(key);
      result.push(trimmed);
    }
    return result;
  }

  private normalizePhoneDigits(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  }

  private maskPhoneNumber(phone: string): string {
    const digits = this.normalizePhoneDigits(phone);
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
  }

  protected async onUpdatePreferences(): Promise<void> {
    this.phoneError.set(null);
    this.emailError.set(null);

    const externalUserId = this.ciamAuthService.getExternalUserId();
    const entraTenantId = this.getEntraTenantId();

    if (!externalUserId || !this.methodsLoaded() || !this.identityVerified()) {
      return;
    }

    this.isLoading.set(true);
    let hasError = false;

    const selectedPhoneId = this.form.get('phoneMethodId')?.value as string;
    const selectedPhone = this.phoneFormOptions().find((m) => m.value === selectedPhoneId);
    const selectedEmailId = this.form.get('emailMethodId')?.value as string;
    const selectedEmail = this.emailFormOptions().find((m) => m.value === selectedEmailId);

    if (
      (this.showPhoneField() && !this.normalizePhoneDigits(selectedPhone?.value ?? '')) ||
      (this.showEmailField() && !selectedEmail?.value.trim())
    ) {
      this.isLoading.set(false);
      return;
    }

    try {
      await this.persistMfaPropertyPriority(externalUserId, selectedPhone?.value, selectedEmail?.value);
    } catch (error) {
      this.isLoading.set(false);
      if (this.showPhoneField()) this.phoneError.set('mfaPreferencesPhoneError');
      if (this.showEmailField()) this.emailError.set('mfaPreferencesEmailError');
      this.loggingService.logException(error, '[MfaPreferences] Failed to save prioritized MFA properties');
      return;
    }

    const phoneFieldName = selectedPhone ? MFA_PHONE_FIELD_NAMES[0] : undefined;

    if (this.showPhoneField() && selectedPhone && phoneFieldName) {
      try {
        await this.customerIdentityService.createPhoneAuthMethod(
          externalUserId,
          phoneFieldName,
          'mobile',
          entraTenantId,
        );
      } catch (error) {
        hasError = true;
        this.phoneError.set('mfaPreferencesPhoneError');
        this.loggingService.logException(error, '[MfaPreferences] Failed to update phone auth method');
      }
    }

    const emailFieldName = selectedEmail ? MFA_EMAIL_FIELD_NAMES[0] : undefined;
    if (this.showEmailField() && selectedEmail && emailFieldName) {
      try {
        await this.customerIdentityService.createEmailAuthMethod(externalUserId, emailFieldName, entraTenantId);
      } catch (error) {
        hasError = true;
        this.emailError.set('mfaPreferencesEmailError');
        this.loggingService.logException(error, '[MfaPreferences] Failed to update email auth method');
      }
    }

    this.isLoading.set(false);

    if (!hasError) {
      this.currentStep.set('success');
    }
  }

  private async persistMfaPropertyPriority(
    externalUserId: string,
    selectedPhone?: string,
    selectedEmail?: string,
  ): Promise<void> {
    const orderedPhones = this.uniquePhones([
      selectedPhone ?? '',
      ...this.servicingPhoneValues,
      ...this.originationPhoneValues,
      ...this.phoneFormOptions().map((option) => option.value),
    ]).slice(0, MFA_PHONE_FIELD_NAMES.length);
    const orderedEmails = this.uniqueEmails([
      selectedEmail ?? '',
      ...this.servicingEmailValues,
      ...this.emailFormOptions().map((option) => option.value),
    ]).slice(0, MFA_EMAIL_FIELD_NAMES.length);

    const properties: Record<string, string> = {};
    this.phoneFieldNameByValue.clear();
    this.emailFieldNameByValue.clear();

    orderedPhones.forEach((value, index) => {
      const fieldName = MFA_PHONE_FIELD_NAMES[index];
      properties[fieldName] = value;
      this.phoneFieldNameByValue.set(value, fieldName);
    });
    orderedEmails.forEach((value, index) => {
      const fieldName = MFA_EMAIL_FIELD_NAMES[index];
      properties[fieldName] = value;
      this.emailFieldNameByValue.set(value, fieldName);
    });

    if (!Object.keys(properties).length) return;

    const response = await this.customerIdentityService.updateUserProfileProperties(
      externalUserId,
      JSON.stringify({ MFA: [properties] }),
    );
    if (!response.success || response.invalidProperties?.trim()) {
      throw new Error(response.invalidProperties || 'Customer Identity rejected the MFA property update.');
    }
  }

  protected onClose(): void {
    if (this.isLoading() || this.mfaLoading() || this.verifyLoading()) return;
    this.currentStep.set('verify');
    this.identityVerified.set(false);
    this.verifyLoading.set(false);
    this.phoneError.set(null);
    this.emailError.set(null);
    this.mfaError.set(null);
    this.methodsLoaded.set(false);
    this.showPhoneField.set(false);
    this.showEmailField.set(false);
    this.phoneFormOptions.set([]);
    this.emailFormOptions.set([]);
    this.defaultPhoneMethodId.set('');
    this.defaultEmailMethodId.set('');
    this.phoneFieldNameByValue.clear();
    this.emailFieldNameByValue.clear();
    this.entraOnlyPhoneValues.clear();
    this.entraOnlyEmailValues.clear();
    this.servicingPhoneValues = [];
    this.servicingEmailValues = [];
    this.originationPhoneValues = [];
    this.closeModal.emit();
  }
}
